import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "./useAuth";
import {
  getSkillSettings,
  listSkillRuns,
  updateSkillSettings,
  type UpdateSkillSettingsInput,
} from "../lib/skills-api";
import { undoSkillRunWithConflictPrompt } from "../lib/skill-undo-flow";
import { invalidateCvDocumentQueries } from "../lib/cv-query-invalidation";

export function useSkillAccessSettings(showRuns: boolean) {
  const { user } = useAuth();
  const { t } = useTranslation();
  const client = useQueryClient();
  const busy = useRef(false);
  const settingsKey = ["skills", "settings", user?.id] as const;
  const runsKey = ["skills", "runs", user?.id] as const;
  const settings = useQuery({
    queryKey: settingsKey,
    queryFn: getSkillSettings,
    enabled: !!user?.id,
  });
  const runs = useQuery({
    queryKey: runsKey,
    queryFn: () => listSkillRuns({ limit: 20 }),
    enabled: !!user?.id && showRuns,
  });

  const save = async (patch: UpdateSkillSettingsInput) => {
    if (busy.current || !settings.data) return false;
    busy.current = true;
    try {
      const next = await updateSkillSettings(patch);
      client.setQueryData(settingsKey, next);
      return true;
    } finally {
      busy.current = false;
    }
  };
  /**
   * True once the run is undone. False when another write is in flight, or
   * when the user kept edits made after the run (B05: the conflict prompt
   * already told them; nothing changed).
   */
  const undo = async (runId: string) => {
    if (busy.current) return false;
    busy.current = true;
    try {
      const outcome = await undoSkillRunWithConflictPrompt(runId, t);
      if (outcome.kind === "kept") return false;
      await Promise.all([
        client.invalidateQueries({ queryKey: runsKey }),
        invalidateCvDocumentQueries(client),
      ]);
      return true;
    } finally {
      busy.current = false;
    }
  };
  return { settings, runs, save, undo };
}
